import React, { useState } from "react";
import { Box, TextField, IconButton, CircularProgress, Snackbar, Alert } from "@mui/material";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SendIcon from "@mui/icons-material/Send";
import axiosInstance from "../api/axiosConfig";
import { useNavigate } from "react-router-dom";
import {
  normalizeDownloadUrl,
  downloadEndpoint,
  downloadErrorMessage,
  siteLabel,
} from "../utils/downloadUtils";

interface Props {
  size?: "small" | "normal";
  onSubmitted?: () => void;
}

const MiniVideoUploader: React.FC<Props> = ({ size = "small", onSubmitted }) => {
  const [urlRaw, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageSeverity, setMessageSeverity] = useState<'info' | 'success' | 'error' | 'warning'>('info');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const navigate = useNavigate();

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text);
    } catch (err) {
      console.error("Clipboard read failed:", err);
      setMessageSeverity('error');
      setMessage("Failed to read clipboard");
    }
  };

  const handleSubmit = async () => {
    const url = normalizeDownloadUrl(urlRaw);
    if (!url) {
      setMessageSeverity("error");
      setMessage("Paste a full video link, e.g. https://x.com/user/status/123");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post(downloadEndpoint(url, backendUrl), { url });

      if (response?.data?.exist && response.data.videoId) {
        // If the video already exists, navigate to it
        navigate(`/video/${response.data.videoId}`);
        return;
      }

      // stay on the page: the video list shows the download progress bar
      setMessageSeverity("success");
      setMessage(`${siteLabel(url)} download started`);
      if (onSubmitted) onSubmitted();
      // notify other components a mini-uploader submission occurred
      try {
        window.dispatchEvent(new Event("miniVideoSubmitted"));
      } catch (e) {
        // ignore
      }
    } catch (err: any) {
      console.error("Mini uploader failed:", err);
      setMessageSeverity("error");
      setMessage(downloadErrorMessage(err, url));
    } finally {
      setLoading(false);
      setUrl("");
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <TextField
        value={urlRaw}
        onChange={(e) => setUrl(e.target.value)}
        size={size === "small" ? "small" : "medium"}
        placeholder="YouTube / X / Facebook / Threads URL"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !loading) handleSubmit();
        }}
        variant="outlined"
        sx={{ width: 200, maxWidth: "25vw" }}
        id="outlined-size-small"
        defaultValue="Small"
      />
      <IconButton size="small" onClick={handlePaste} aria-label="paste">
        <FileCopyIcon fontSize="small" />
      </IconButton>
      
    <IconButton 
        onClick={handleSubmit}
        disabled={loading || !urlRaw}
        size="small"
        color="primary"
        aria-label="submit"
    >
        {loading ? <CircularProgress size={18} /> : <SendIcon fontSize="small"/>}
    </IconButton>
      <Snackbar open={!!message} autoHideDuration={3000} onClose={() => { setMessage(""); setMessageSeverity('info'); }}>
        <Alert onClose={() => { setMessage(""); setMessageSeverity('info'); }} severity={messageSeverity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MiniVideoUploader;
